import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { RotateCcw } from 'lucide-react';

const AnnotationCanvas = forwardRef(({ width, height, isDrawingMode }, ref) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const contextRef = useRef(null);

    useImperativeHandle(ref, () => ({
        clearCanvas: () => {
            const canvas = canvasRef.current;
            const context = canvas.getContext("2d");
            context.clearRect(0, 0, canvas.width, canvas.height);
        },
        getDataURL: () => {
            return canvasRef.current.toDataURL("image/png");
        },
        isEmpty: () => {
            const canvas = canvasRef.current;
            const blank = document.createElement('canvas');
            blank.width = canvas.width;
            blank.height = canvas.height;
            return canvas.toDataURL() === blank.toDataURL();
        }
    }));

    useEffect(() => {
        const canvas = canvasRef.current;
        canvas.width = width;
        canvas.height = height;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const context = canvas.getContext("2d");
        context.scale(1, 1);
        context.lineCap = "round";
        context.strokeStyle = "#ef4444"; // Red color
        context.lineWidth = 4;
        contextRef.current = context;
    }, [width, height]);

    const startDrawing = ({ nativeEvent }) => {
        if (!isDrawingMode) return;
        const { offsetX, offsetY } = nativeEvent;
        contextRef.current.beginPath();
        contextRef.current.moveTo(offsetX, offsetY);
        setIsDrawing(true);
    };

    const finishDrawing = () => {
        contextRef.current.closePath();
        setIsDrawing(false);
    };

    const draw = ({ nativeEvent }) => {
        if (!isDrawing || !isDrawingMode) return;
        const { offsetX, offsetY } = nativeEvent;
        contextRef.current.lineTo(offsetX, offsetY);
        contextRef.current.stroke();
    };

    return (
        <div className="relative" style={{ width, height }}>
            <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseUp={finishDrawing}
                onMouseMove={draw}
                onMouseLeave={finishDrawing}
                className={`absolute top-0 left-0 z-20 ${isDrawingMode ? 'cursor-crosshair' : 'pointer-events-none'}`}
            />

            {/* Simple Tools Overlay (Only visible when drawing) */}
            {isDrawingMode && (
                <div className="absolute top-4 right-4 z-30 flex gap-2">
                    <button
                        onClick={() => ref.current.clearCanvas()}
                        className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                        title="Clear Drawing"
                    >
                        <RotateCcw size={16} />
                    </button>
                    {/* Add more colors later if needed */}
                </div>
            )}
        </div>
    );
});

export default AnnotationCanvas;
